import { createSelector } from 'reselect';
import { StoreStructure } from '../types/types';
import { get, parseXpath } from './lodash';
import { analyzeTransient } from './transient';

export const generateRawSelectors = <T extends StoreStructure>(storeState: T) => {
  const output = {} as {
    [K in keyof T as `$raw${Capitalize<string & K>}`]: (state: T) => T[K];
  };

  const keys = Object.keys(storeState) as Array<keyof T>;
  const transient = analyzeTransient(storeState);

  for (const key of keys) {
    const isTransient = Object.keys(transient).includes(key as string);
    const selectorKey = `$raw${capitalize(key as string)}` as `$raw${Capitalize<
      string & typeof key
    >}`;

    // @ts-ignore
    output[selectorKey] = isTransient
      ? generateTransientSelector<T, typeof key>(key, transient)
      : generateSelector<T, typeof key>(key);
  }

  // @ts-ignore
  output['$i'] = (state: T) => state;

  return output;
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const generateSelector = <T extends StoreStructure, K extends keyof T>(key: K) => {
  return createSelector(
    (state: T) => state[key], // Extract the specific key's value directly
    (slice: T[K]) => slice // Explicitly type the return value
  );
};

export const generateTransientSelector = <T extends StoreStructure, K extends keyof T>(
  key: K,
  transient: any
) => {
  return createSelector(
    (state: T) => state, // Extract the specific key's value directly
    (state: T) => state.currentIds, // Extract the specific key's value directly
    (state, currentIds) => {
      const xpathRaw = transient[key];

      const xpath = parseXpath(xpathRaw, currentIds);

      const slice = get(state, xpath) ?? {};
      return slice;
    }
  );
};
